import type { CSSProperties } from "react";
import { Fragment } from "react";
import { isEmpty } from "lodash";
import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import { useFetchNotifications } from "@/react/components/shared/uikit/HeaderV3/RightActionBlock/Notifications/useHeaderNotificationsApi";
import { usePunditUserContext } from "@circle-react/contexts";
import { Loader } from "@circle-react-uikit/Loader";
import { Typography } from "@circle-react-uikit/Typography";
import { useNotificationContext } from "../NotificationContext";
import { ListWrapper } from "./ListWrapper";
import { NoNotifications } from "./NoNotifications";
import { NotificationItem } from "./NotificationItem";
import { SkeletonLoader } from "./SkeletonLoader";

export interface NotificationsListProps {
  className?: string;
  isFullPage?: boolean;
  style?: CSSProperties;
}

export const NotificationsList = ({
  className,
  style,
  isFullPage = false,
}: NotificationsListProps) => {
  const { activeTab, lastMarkedAsRead } = useNotificationContext();
  const { isLoading, notifications, fetchNextPage, hasNextPage } =
    useFetchNotifications({
      notificationGroup: activeTab.toLocaleLowerCase(),
    });

  const { currentCommunity } = usePunditUserContext();

  const current_plan_tier = currentCommunity?.current_plan_tier;

  if (isLoading)
    return (
      <ListWrapper className={className} style={style} isFullPage={isFullPage}>
        <SkeletonLoader rows={15} />
      </ListWrapper>
    );

  if (
    !notifications ||
    !notifications.pages[0] ||
    isEmpty(notifications.pages[0].records)
  ) {
    return <NoNotifications className={className} style={style} />;
  }

  return (
    <ListWrapper className={className} style={style} isFullPage={isFullPage}>
      <InfiniteScroll
        style={{
          overflow: "auto",
        }}
        scrollThreshold={0.8}
        next={fetchNextPage}
        hasMore={hasNextPage}
        dataLength={notifications.pages.length}
        scrollableTarget={
          isFullPage ? "panel" : "user-notifications-panel-header"
        }
        loader={<Loader center />}
      >
        {notifications.pages.map(page =>
          page.records.map(notification => (
            <Fragment key={notification.id}>
              <div className="hover:bg-tertiary focus-visible:bg-tertiary group flex w-full items-start justify-between rounded-md px-4 py-3 transition-colors duration-200 ease-in-out">
                <NotificationItem
                  notification={notification}
                  lastMarkedAsRead={lastMarkedAsRead}
                />
              </div>
              <div className="bg-secondary-dark mx-2 h-[1px] last:hidden" />
            </Fragment>
          )),
        )}
      </InfiniteScroll>
      {current_plan_tier && (
        <div className="w-full py-2 text-center">
          <Typography.LabelXs>
            {t("notification_retention_message", {
              months: t(`notification_retention_months.${current_plan_tier}`),
            })}
          </Typography.LabelXs>
        </div>
      )}
    </ListWrapper>
  );
};
